import React, {
  useState,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Upload,
  CheckCircle,
  Trash2,
  Edit,
  Info,
  X,
  MapPin,
  Building,
} from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "../../components/AdminSharedComponents";
import DropdownField from "../../components/ui/Form/DropdownField";
import { createCustomIcon } from "../ExcavationMap/utils";

const ExcavationBulkForm = forwardRef(
  (
    {
      projects,
      selectedProject,
      setSelectedProject,
      bulkPoints,
      setBulkPoints,
      bulkCreating,
      setBulkCreating,
      onSubmit,
      isProjectPreFilled = false,
      hasDraft = false,
      onClearDraft,
    },
    ref
  ) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const projectMarkerRef = useRef(null);
    const lastProjectIdRef = useRef(null); // Track last zoomed project to prevent loops
    const [gpsFile, setGpsFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [mapMarkers, setMapMarkers] = useState([]);
    const [editingPoint, setEditingPoint] = useState(null);

    // Project options
    const projectOptions = projects.map((project) => ({
      value: project.id,
      label: project.name,
    }));

    // Helper function to create project marker icon with opacity
    const createProjectMarkerIcon = (opacity = 1) => {
      return L.divIcon({
        html: `<div style="
          width: 30px;
          height: 30px;
          background-color: #3b82f6;
          border: 3px solid #ffffff;
          border-radius: 50%;
          box-shadow: 0 2px 12px rgba(0,0,0,0.3);
          position: relative;
          opacity: ${opacity};
          transition: opacity 0.3s ease;
        ">
          <div style="
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 12px;
            height: 12px;
            background-color: #ffffff;
            border-radius: 50%;
          "></div>
        </div>`,
        className: "project-location-marker",
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        popupAnchor: [0, -13],
      });
    };

    // Helper function to update project marker opacity
    const updateProjectMarkerOpacity = useCallback((opacity) => {
      if (projectMarkerRef.current && mapInstanceRef.current) {
        const newIcon = createProjectMarkerIcon(opacity);
        projectMarkerRef.current.setIcon(newIcon);
      }
    }, []);

    // Expose handleBulkCreate to parent component
    useImperativeHandle(ref, () => ({
      submitBulkCreate: handleBulkCreate,
    }));

    // Initialize map
    const initializeMap = useCallback(() => {
      if (!mapRef.current || mapInstanceRef.current) return;

      // Default center (Indonesia)
      const defaultCenter = [-2.5489, 118.0149];
      mapInstanceRef.current = L.map(mapRef.current).setView(defaultCenter, 5);

      // Define tile layers
      const cartoDBLayer = L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 20,
        }
      );

      const openStreetMapLayer = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
        }
      );

      // Add CartoDB as default layer
      cartoDBLayer.addTo(mapInstanceRef.current);

      // Add layer control for switching
      const baseLayers = {
        "CartoDB Light": cartoDBLayer,
        OpenStreetMap: openStreetMapLayer,
      };
      L.control.layers(baseLayers).addTo(mapInstanceRef.current);
    }, []);

    // Clear all map markers
    const clearMapMarkers = useCallback(() => {
      if (mapInstanceRef.current && mapMarkers.length > 0) {
        mapMarkers.forEach((marker) => {
          mapInstanceRef.current.removeLayer(marker);
        });
        setMapMarkers([]);

        // Restore project marker opacity (kembali ke normal karena bulk points dihapus)
        updateProjectMarkerOpacity(1);
      }
    }, [mapMarkers, updateProjectMarkerOpacity]);

    // Fly to project location
    const flyToProjectLocation = useCallback(
      (projectId) => {
        if (!mapInstanceRef.current || !projectId) return;

        // Find the selected project
        const project = projects.find(
          (p) => String(p.id) === String(projectId)
        );
        if (!project || !project.coordinates) return;

        // Parse coordinates
        let coords = null;
        if (
          project.coordinates.coordinates &&
          Array.isArray(project.coordinates.coordinates)
        ) {
          // PostGIS format: {coordinates: [lng, lat]}
          coords = {
            lat: project.coordinates.coordinates[1],
            lng: project.coordinates.coordinates[0],
          };
        } else if (project.coordinates.lat && project.coordinates.lng) {
          // Direct format: {lat, lng}
          coords = {
            lat: project.coordinates.lat,
            lng: project.coordinates.lng,
          };
        }

        if (!coords) return;

        // Remove existing project marker if any
        if (projectMarkerRef.current) {
          mapInstanceRef.current.removeLayer(projectMarkerRef.current);
          projectMarkerRef.current = null;
        }

        // Focus to project location instantly (no animation to prevent "goyang-goyang")
        mapInstanceRef.current.setView([coords.lat, coords.lng], 15);

        // Determine opacity: samar (0.35) jika bulk points sudah di-upload, normal (1) jika belum
        const opacity = bulkPoints.length > 0 ? 0.35 : 1;

        // Add project location marker dengan opacity sesuai state
        const projectIcon = createProjectMarkerIcon(opacity);
        projectMarkerRef.current = L.marker([coords.lat, coords.lng], {
          icon: projectIcon,
          zIndexOffset: 1000, // Ensure project marker appears above excavation markers
        }).addTo(mapInstanceRef.current);

        // Add popup to project marker
        projectMarkerRef.current.bindPopup(
          `<div class="p-3">
          <h4 class="font-semibold text-sm text-blue-700 mb-2">📍 Project Location</h4>
          <p class="text-sm text-gray-900 font-medium mb-1">${project.name}</p>
          <p class="text-xs text-gray-500 mb-2">Lat: ${coords.lat.toFixed(
            6
          )}, Lng: ${coords.lng.toFixed(6)}</p>
          <p class="text-xs text-orange-600 mt-2 pt-2 border-t border-gray-200">💡 Upload excavation points di sekitar lokasi ini</p>
        </div>`,
          { closeButton: false }
        );

        // Auto-open popup for 3 seconds
        projectMarkerRef.current.openPopup();
        setTimeout(() => {
          if (projectMarkerRef.current) {
            projectMarkerRef.current.closePopup();
          }
        }, 3000);
      },
      [projects, bulkPoints]
    );

    React.useEffect(() => {
      const timer = setTimeout(() => {
        initializeMap();
      }, 100);

      return () => clearTimeout(timer);
    }, [initializeMap]);

    // Fly to project location when project is selected (prevent infinite loop)
    React.useEffect(() => {
      if (selectedProject && mapInstanceRef.current) {
        // Only fly if project changed (prevent infinite loop)
        if (lastProjectIdRef.current !== selectedProject) {
          flyToProjectLocation(selectedProject);
          lastProjectIdRef.current = selectedProject;
        }
      }
    }, [selectedProject, flyToProjectLocation]);

    // Restore bulk points on map after initialization
    React.useEffect(() => {
      if (
        mapInstanceRef.current &&
        bulkPoints.length > 0 &&
        !mapMarkers.length
      ) {
        // Display restored points on map
        displayPointsOnMap(bulkPoints);
      }
    }, [mapInstanceRef.current, bulkPoints.length]); // eslint-disable-line react-hooks/exhaustive-deps

    // Display points on map
    const displayPointsOnMap = (points) => {
      if (!mapInstanceRef.current || !points || points.length === 0) return;

      // Clear existing excavation markers (but keep project marker)
      clearMapMarkers();

      const markers = [];
      let bounds = L.latLngBounds();

      // Include project location in bounds if it exists
      if (projectMarkerRef.current) {
        const projectLatLng = projectMarkerRef.current.getLatLng();
        bounds.extend([projectLatLng.lat, projectLatLng.lng]);
      }

      points.forEach((point, index) => {
        if (
          point.coordinates &&
          point.coordinates.lat &&
          point.coordinates.lng
        ) {
          const icon = createCustomIcon(point.status || "Planned");
          const marker = L.marker(
            [point.coordinates.lat, point.coordinates.lng],
            {
              icon: icon,
            }
          );

          // Add popup with point info
          const popupContent = `
          <div class="p-2">
            <h4 class="font-semibold text-sm">${
              point.name || `Point ${index + 1}`
            }</h4>
            <p class="text-xs text-gray-600 mt-1">
              Lat: ${point.coordinates.lat.toFixed(6)}<br>
              Lng: ${point.coordinates.lng.toFixed(6)}
            </p>
            ${
              point.description
                ? `<p class="text-xs text-gray-500 mt-1">${point.description}</p>`
                : ""
            }
            ${
              point.depth
                ? `<p class="text-xs text-blue-600 mt-1">Depth: ${point.depth}m</p>`
                : ""
            }
          </div>
        `;
          marker.bindPopup(popupContent);

          marker.addTo(mapInstanceRef.current);
          markers.push(marker);
          bounds.extend([point.coordinates.lat, point.coordinates.lng]);
        }
      });

      setMapMarkers(markers);

      // Update project marker opacity (dibuat samar karena bulk points sudah di-render)
      updateProjectMarkerOpacity(0.35);

      // Fit map to show all points
      if (bounds.isValid()) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });
      }
    };

    // Handle GPS file upload
    const handleFileUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      setGpsFile(file);
      setUploading(true);

      try {
        const formData = new FormData();
        formData.append("gps_file", file);

        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:5000";
        const response = await fetch(
          `${API_URL}/api/excavation-points/upload-gps`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.ok) {
          const result = await response.json();

          if (
            result.data &&
            result.data.points &&
            result.data.points.length > 0
          ) {
            const points = result.data.points;
            setBulkPoints(points);
            displayPointsOnMap(points);
          } else {
            throw new Error("No valid GPS points found in the uploaded file");
          }
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to process GPS file");
        }
      } catch (error) {
        console.error("GPS upload error:", error);
        alert(`Failed to process GPS file: ${error.message}`);
      } finally {
        setUploading(false);
      }
    };

    // Handle bulk creation
    const handleBulkCreate = useCallback(async () => {
      if (bulkPoints.length === 0) {
        alert("No points to create");
        return;
      }

      if (!selectedProject) {
        alert("Please select a project first");
        return;
      }

      setBulkCreating(true);
      try {
        const API_URL =
          process.env.REACT_APP_API_URL || "http://localhost:5000";
        const response = await fetch(
          `${API_URL}/api/excavation-points/bulk-create`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              project_id: selectedProject,
              points: bulkPoints,
              default_status: "Planned",
            }),
          }
        );

        if (response.ok) {
          const result = await response.json();

          alert(
            `Successfully created ${result.data.totalCreated} out of ${result.data.totalRequested} excavation points!`
          );

          // Clear data
          setBulkPoints([]);
          setGpsFile(null);
          clearMapMarkers();

          // Remove project marker
          if (projectMarkerRef.current && mapInstanceRef.current) {
            mapInstanceRef.current.removeLayer(projectMarkerRef.current);
            projectMarkerRef.current = null;
          }

          // Reset last project ID
          lastProjectIdRef.current = null;

          // Call parent onSubmit callback
          if (onSubmit) {
            onSubmit();
          }
        } else {
          const errorData = await response.json();
          throw new Error(
            errorData.message || "Failed to create excavation points"
          );
        }
      } catch (error) {
        console.error("Bulk creation error:", error);
        alert(`Error creating excavation points: ${error.message}`);
      } finally {
        setBulkCreating(false);
      }
    }, [
      bulkPoints,
      selectedProject,
      setBulkCreating,
      setBulkPoints,
      setGpsFile,
      clearMapMarkers,
      onSubmit,
    ]);

    // Edit point
    const handleEditPoint = (index) => {
      setEditingPoint({ index, point: bulkPoints[index] });
    };

    // Update point
    const handleUpdatePoint = (updatedPoint) => {
      const newPoints = [...bulkPoints];
      newPoints[editingPoint.index] = updatedPoint;
      setBulkPoints(newPoints);
      setEditingPoint(null);
      displayPointsOnMap(newPoints);
    };

    // Remove point
    const handleRemovePoint = (index) => {
      const newPoints = bulkPoints.filter((_, i) => i !== index);
      setBulkPoints(newPoints);
      displayPointsOnMap(newPoints);
    };

    // Clear all data
    const handleClearAll = () => {
      if (onClearDraft) {
        // Use parent's clear draft function (includes localStorage cleanup)
        onClearDraft();
      } else {
        // Fallback if callback not provided
        setBulkPoints([]);
        setSelectedProject("");
      }

      setGpsFile(null);
      clearMapMarkers();

      // Remove project marker if exists
      if (projectMarkerRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeLayer(projectMarkerRef.current);
        projectMarkerRef.current = null;
      }

      // Reset last project ID
      lastProjectIdRef.current = null;

      // Reset map view to default
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setView([-2.5489, 118.0149], 5);
      }
    };

    return (
      <div className="space-y-8">
        {/* Draft Restored Notification - Only show if has valuable data */}
        {hasDraft && !isProjectPreFilled && bulkPoints.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Draft tersimpan dikembalikan
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {bulkPoints.length} point(s) yang sudah di-upload telah
                  dipulihkan
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={onClearDraft}
              disabled={bulkCreating}
              className="flex-shrink-0"
            >
              <X className="h-4 w-4 mr-1" />
              Mulai Baru
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Upload & Configuration */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Selection */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Building className="h-5 w-5 text-primary" />
                  Project Selection
                </h2>
              </div>
              <div className="p-5">
                {isProjectPreFilled && (
                  <div className="mb-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        Project sudah dipilih otomatis
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Semua titik galian akan ditambahkan ke project yang
                        dipilih
                      </p>
                    </div>
                  </div>
                )}
                <DropdownField
                  label="Select Project"
                  name="project_id"
                  options={projectOptions}
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  placeholder="Choose project for all points"
                  required
                  disabled={bulkCreating || isProjectPreFilled}
                />

                {selectedProject &&
                  projects.find((p) => String(p.id) === String(selectedProject))
                    ?.coordinates && (
                    <div className="mt-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                      <MapPin className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-green-900">
                          Lokasi project ditampilkan di map
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Upload excavation points di sekitar marker biru
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* GPS File Upload */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  GPS File Upload
                </h2>
              </div>
              <div className="p-5">
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept=".gpx,.kml,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="gps-upload"
                      disabled={bulkCreating || uploading}
                    />
                    <label htmlFor="gps-upload" className="cursor-pointer">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">
                        {uploading
                          ? "Processing GPS file..."
                          : gpsFile
                          ? gpsFile.name
                          : "Click to upload GPS file"}
                      </p>
                      <p className="text-xs text-gray-500">
                        Supports .gpx, .kml, .csv files from Garmin GPS
                      </p>
                    </label>
                  </div>

                  {gpsFile && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <p className="text-sm text-green-800">
                          GPS file processed successfully
                        </p>
                      </div>
                      <p className="text-xs text-green-700 mt-1">
                        {bulkPoints.length} points found and displayed on map
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Points Summary */}
            {bulkPoints.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      Points Summary
                    </h2>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleClearAll}
                      disabled={bulkCreating}
                      icon={Trash2}
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Points:</span>
                      <span className="font-semibold">{bulkPoints.length}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Project:</span>
                      <span className="font-semibold">
                        {projects.find((p) => p.id === selectedProject)?.name ||
                          "Not selected"}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {bulkPoints.map((point, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 border border-gray-200 rounded p-3 text-xs"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-gray-900">
                            {point.name || `Point ${index + 1}`}
                          </span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleEditPoint(index)}
                              disabled={bulkCreating}
                              className="p-1 h-6 w-6"
                              icon={Edit}
                            />
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleRemovePoint(index)}
                              disabled={bulkCreating}
                              className="p-1 h-6 w-6"
                              icon={Trash2}
                            />
                          </div>
                        </div>
                        <div className="text-gray-600">
                          Lat: {point.coordinates.lat.toFixed(6)}, Lng:{" "}
                          {point.coordinates.lng.toFixed(6)}
                        </div>
                        {point.description && (
                          <div className="text-gray-500 mt-1">
                            {point.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Map */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 sticky top-4 z-10 overflow-hidden">
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Map Preview
                </h2>
              </div>
              <div className="p-6">
                <div className="border border-gray-300 rounded-lg overflow-hidden shadow-inner h-[600px]">
                  <div ref={mapRef} className="w-full h-full" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Point Edit Modal */}
        {editingPoint && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Edit Point {editingPoint.index + 1}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Point Name
                  </label>
                  <input
                    type="text"
                    value={editingPoint.point.name || ""}
                    onChange={(e) =>
                      setEditingPoint({
                        ...editingPoint,
                        point: { ...editingPoint.point, name: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={editingPoint.point.description || ""}
                    onChange={(e) =>
                      setEditingPoint({
                        ...editingPoint,
                        point: {
                          ...editingPoint.point,
                          description: e.target.value,
                        },
                      })
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Depth (meters)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={editingPoint.point.depth || ""}
                    onChange={(e) =>
                      setEditingPoint({
                        ...editingPoint,
                        point: { ...editingPoint.point, depth: e.target.value },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setEditingPoint(null)}
                >
                  Cancel
                </Button>
                <Button onClick={() => handleUpdatePoint(editingPoint.point)}>
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

ExcavationBulkForm.displayName = "ExcavationBulkForm";

export default ExcavationBulkForm;
